import { useState } from "react";
import {
  formatAddress,
  formatDateOnly,
  publicAddressLooksInternal,
  publicDisplayName,
  publicMemberSupportContact,
  workspaceNoun
} from "../public-site/publicSiteUtils";
import { MemberPortalSectionTabs } from "./MemberPortalTabs";
import {
  memberStatusLabel,
  RELEASE_FORM_ACTION_LABEL,
  waiverIsRequired,
  waiverStatusCopy
} from "./memberPortalUtils";

export function MemberPortalSupportPanel({
  member = null,
  memberAccountDetail = "",
  memberAccountLabel = "Signed in",
  onSignOut,
  range = {}
}) {
  const [supportSection, setSupportSection] = useState("contact");
  const workspace = workspaceNoun(range, "range");
  const displayName = publicDisplayName(range) || `this ${workspace}`;
  const profile = range?.profile || {};
  const supportContact = publicMemberSupportContact(range);
  const contactEmail = supportContact.email;
  const contactPhone = supportContact.phone;
  const formattedAddress = formatAddress(profile.address);
  const publicAddress = publicAddressLooksInternal(formattedAddress) ? "" : formattedAddress;
  const supportSubject = encodeURIComponent(`Member portal help - ${displayName}`);
  const supportBody = encodeURIComponent(`Hi ${displayName} team,\n\nI need help with my member portal.\n\nWhat I need help with:\n`);
  const hasContactPath = Boolean(contactEmail || contactPhone);
  const memberState = member
    ? member.status === "active"
      ? "Membership active"
      : memberStatusLabel(member.status)
    : "No member record";
  const memberPlanSummary = member?.membershipPlan?.name
    ? `${member.membershipPlan.name}${member?.membership?.currentTermEnd ? ` through ${formatDateOnly(member.membership.currentTermEnd)}` : ""}`
    : member
      ? `${memberStatusLabel(member.status)} membership`
      : "Sign in or start membership to see account details.";
  const requirements = member?.review?.requirements || {};
  const waiver = requirements.waiver || {};
  const supportNextStep = !member
    ? {
        title: "Connect your membership",
        body: `Use the same account you used at signup, or ask the ${workspace} to link your member record.`,
        detail: "Membership link needed"
      }
    : waiverIsRequired(waiver) && waiver.status !== "completed"
      ? {
          title: "Release form needed",
          body: waiverStatusCopy(waiver).detail,
          detail: "Sign before your next visit",
          href: "#member-documents"
        }
      : member.status === "active"
        ? {
            title: "Plan your next visit",
            body: "Use Events or Visits for schedules, registrations, and booking context.",
            detail: "Member access active",
            href: "#events"
          }
        : {
            title: `Watch for ${workspace} approval`,
            body: `Your payment is recorded. The ${workspace} team will activate access after final review.`,
            detail: "No action needed right now"
          };
  const supportContactGuidance = !member
    ? {
        title: "Membership not showing?",
        body: `Contact the ${workspace} if you paid or signed up with a different email and this account is not showing your record.`,
        detail: "Account link help"
      }
    : waiverIsRequired(waiver) && waiver.status !== "completed"
      ? {
          title: "Release form trouble?",
          body: `Contact the ${workspace} if the form link is missing, the wrong document is shown, or you already signed elsewhere.`,
          detail: "Document help"
        }
      : member.status === "active"
        ? {
            title: "Need a change?",
            body: `Contact the ${workspace} for billing changes, booking adjustments, access problems, or questions before your next visit.`,
            detail: "Member support"
          }
        : {
            title: "Details look wrong?",
            body: `Contact the ${workspace} if the payment, plan, name, or email on this page does not match what you expect.`,
            detail: "Correction help"
          };

  return (
    <section className="member-portal-support-panel" id="member-support">
      <div className="member-portal-dashboard-header">
        <div>
          <span className="public-section-kicker">Support</span>
          <h2>Help and contact</h2>
          <p>Start with the right next step, then contact the {workspace} when you need staff help.</p>
        </div>
      </div>

      <MemberPortalSectionTabs
        activeSection={supportSection}
        onSelect={setSupportSection}
        sections={[
          { id: "contact", label: "Contact", detail: `${workspace} support` },
          { id: "requests", label: "Requests", detail: "Where to start" },
          { id: "account", label: "Account", detail: "Session details" }
        ]}
      />

      {supportSection === "contact" && (
        <>
          <div className="member-portal-status-grid">
            <div>
              <span>Next step</span>
              <strong>{supportNextStep.title}</strong>
              <small>{supportNextStep.detail}</small>
              {supportNextStep.href && (
                <a className="secondary-button" href={supportNextStep.href}>
                  {supportNextStep.href === "#member-documents" ? RELEASE_FORM_ACTION_LABEL : supportNextStep.title}
                </a>
              )}
            </div>
            <div>
              <span>Contact</span>
              <strong>{hasContactPath ? "Available" : "Not published yet"}</strong>
              <small>{contactEmail || contactPhone || `Ask the ${workspace} for the current support path.`}</small>
            </div>
            <div>
              <span>Membership</span>
              <strong>{memberState}</strong>
              <small>{memberPlanSummary}</small>
            </div>
          </div>
          <div className="member-portal-split-list">
            <div>
              <h3>Contact the {workspace}</h3>
              <article>
                <strong>{displayName}</strong>
                <span>{hasContactPath ? `Use this contact path when your question needs ${workspace} staff help.` : `This ${workspace} has not published member support contact details in the portal yet.`}</span>
                {publicAddress && <span>{publicAddress}</span>}
                <em>{hasContactPath ? "Support" : "Ask at check-in"}</em>
                {hasContactPath && (
                  <div className="member-portal-support-actions">
                    {contactEmail && (
                      <a className="secondary-button" href={`mailto:${contactEmail}?subject=${supportSubject}&body=${supportBody}`}>
                        Email {workspace}
                      </a>
                    )}
                    {contactPhone && <a className="secondary-button" href={`tel:${contactPhone}`}>Call {workspace}</a>}
                  </div>
                )}
              </article>
            </div>
            <div>
              <h3>When to reach out</h3>
              <article>
                <strong>{supportContactGuidance.title}</strong>
                <span>{supportContactGuidance.body}</span>
                <em>{supportContactGuidance.detail}</em>
              </article>
            </div>
          </div>
        </>
      )}

      {supportSection === "requests" && (
        <div className="member-portal-split-list">
          <div>
            <h3>Common requests</h3>
            <article>
              <strong>Payments or receipts</strong>
              <span>Check Payments first for balances and receipts. Contact the {workspace} for refunds, expired payment windows, or charges you do not recognize.</span>
              <em>Start in Payments</em>
              <div className="member-portal-support-actions">
                <a className="secondary-button" href="#member-payments">Open Payments</a>
              </div>
            </article>
            <article>
              <strong>Bookings, classes, or access</strong>
              <span>Use Classes or Visits for schedule context, then contact the {workspace} for reservation changes, waitlists, orientation timing, or qualification gates.</span>
              <em>Use member tabs first</em>
              <div className="member-portal-support-actions">
                <a className="secondary-button" href="#events">Open Events</a>
              </div>
            </article>
            <article>
              <strong>Documents</strong>
              <span>Use Documents for release-form status and shared files. Contact the {workspace} when a file is missing or something needs to be re-sent.</span>
              <em>Start in Documents</em>
              <div className="member-portal-support-actions">
                <a className="secondary-button" href="#member-documents">Open Documents</a>
              </div>
            </article>
          </div>
        </div>
      )}

      {supportSection === "account" && (
        <div className="member-portal-split-list">
          <div>
            <h3>Account and session</h3>
            <article>
              <strong>{memberAccountLabel}</strong>
              <span>{memberAccountDetail || "Signed in to the member portal."}</span>
              <em>Use the same account for this {workspace} portal</em>
            </article>
            <article>
              <strong>Shared device?</strong>
              <span>Sign out after checking your member information on a shared or public computer.</span>
              <em>Account action only</em>
              {onSignOut && (
                <div className="member-portal-support-actions">
                  <button type="button" className="secondary-button" onClick={onSignOut}>
                    Sign out
                  </button>
                </div>
              )}
            </article>
          </div>
        </div>
      )}
    </section>
  );
}
